import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTenant } from '../../contexts/TenantContext';

interface TenantBackdropProps {
  isDarkMode?: boolean;
}

/**
 * Full-bleed tenant cover image with a legibility overlay.
 *
 * Renders nothing on platform or when the tenant hasn't uploaded a cover.
 * Should be placed as the first child of a `position: relative` page
 * container; foreground content must use `z-10` to render above the overlay.
 */
export const TenantBackdrop: React.FC<TenantBackdropProps> = ({
  isDarkMode: isDarkModeProp,
}) => {
  const theme = useTheme();
  const { tenant } = useTenant();
  const isDarkMode = isDarkModeProp ?? theme.isDarkMode;

  if (!tenant?.cover_image_url) return null;

  return (
    <div className="absolute inset-0 z-0" aria-hidden="true">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${tenant.cover_image_url})` }}
      />
      {/* Legibility overlay — heavier on dark mode so foreground text stays
          AA-contrast regardless of what the tenant uploaded. */}
      <div
        className={
          isDarkMode
            ? 'absolute inset-0 bg-gradient-to-b from-[#030712]/80 via-[#030712]/70 to-[#030712]/90 backdrop-blur-sm'
            : 'absolute inset-0 bg-gradient-to-b from-white/75 via-white/65 to-white/85 backdrop-blur-sm'
        }
      />
    </div>
  );
};

export default TenantBackdrop;
